import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import { stringToNumber } from '../../utils/ids-string-utils/ids-string-utils';
import IdsElement from '../../core/ids-element';

import styles from './ids-layout-flex-item.scss';

// List of flex item options
export const FLEX_ITEM_OPTIONS = {
  alignSelf: ['auto', 'baseline', 'center', 'stretch', 'flex-start', 'flex-end'],
  grow: [0, 1],
  overflow: ['auto', 'hidden', 'visible', 'scroll'],
  shrink: [0, 1]
};

/**
 * IDS Layout Flex Item Component
 * @type {IdsLayoutFlexItem}
 * @inherits IdsElement
 */
@customElement('ids-layout-flex-item')
@scss(styles)
export default class IdsLayoutFlexItem extends IdsElement {
  #gridObserver: MutationObserver | null = null;

  #slotElement: HTMLSlotElement | null = null;

  constructor() {
    super();
  }

  static get attributes(): any {
    return [
      ...super.attributes,
      attributes.ALIGN_SELF,
      attributes.FLEX,
      attributes.GROW,
      attributes.SHRINK,
      attributes.MIN_WIDTH,
      attributes.PADDING_X,
      attributes.PADDING_Y
    ];
  }

  connectedCallback() {
    super.connectedCallback();
    this.#attachSlotChangeHandler();
  }

  disconnectedCallback() {
    this.#slotElement?.assignedElements().forEach((el) => {
      el.classList.remove('in-flex-item');
    });
    this.#gridObserver?.disconnect();
    this.#gridObserver = null;
    super.disconnectedCallback?.();
  }

  /**
   * Check if slot contains ids-layout-grid with enable-container
   * @returns {boolean} True if container grid is found
   */
  #checkForContainerGrid(): boolean {
    if (!this.#slotElement) return false;
    const assignedElements = this.#slotElement.assignedElements();
    return assignedElements.some((el) => el.tagName === 'IDS-LAYOUT-GRID'
      && el.hasAttribute('enable-container'));
  }

  /**
   * Update the has-container-grid class based on slotted content
   */
  #updateContainerGridClass(): void {
    const hasContainerGrid = this.#checkForContainerGrid();
    this.classList.toggle('has-container-grid', hasContainerGrid);
  }

  /**
   * Attach handler for slot changes
   */
  #attachSlotChangeHandler(): void {
    this.#slotElement = this.shadowRoot?.querySelector('slot') || null;
    if (!this.#slotElement) return;

    this.#slotElement.addEventListener('slotchange', () => {
      this.#updateContainerGridClass();
      this.#observeGridAttributes();
      this.#markSlottedChildren();
      this.#updateHasCheckbox();
    });

    // Initial check
    this.#updateContainerGridClass();
    this.#observeGridAttributes();
    this.#markSlottedChildren();
    this.#updateHasCheckbox();
  }

  /**
   * Mark slotted children with in-flex-item attribute
   */
  #markSlottedChildren(): void {
    if (!this.#slotElement) return;
    const assignedElements = this.#slotElement.assignedElements();
    assignedElements.forEach((el) => {
      el.classList.toggle('in-flex-item', true);
    });
  }

  /**
   * Toggle has-checkbox class when a slotted descendant is an ids-checkbox
   */
  #updateHasCheckbox(): void {
    if (!this.#slotElement) return;
    const hasCheckbox = this.#slotElement
      .assignedElements()
      .some((el) => el.tagName === 'IDS-CHECKBOX');
    this.classList.toggle('has-checkbox', hasCheckbox);
  }

  /**
   * Observe enable-container attribute changes on slotted grids
   */
  #observeGridAttributes(): void {
    // Disconnect existing observer
    this.#gridObserver?.disconnect();

    const grids = this.#slotElement?.assignedElements().filter(
      (el) => el.tagName === 'IDS-LAYOUT-GRID'
    );

    if (!grids?.length) return;

    this.#gridObserver = new MutationObserver(() => {
      this.#updateContainerGridClass();
    });

    grids.forEach((grid) => {
      this.#gridObserver!.observe(grid, {
        attributes: true,
        attributeFilter: ['enable-container']
      });
    });
  }

  template(): string {
    return `<slot></slot>`;
  }

  /**
   * Set the align self setting
   * @param {string} value The value
   */
  set alignSelf(value: string | null) {
    if (value && FLEX_ITEM_OPTIONS.alignSelf.includes(value)) {
      this.setAttribute(attributes.ALIGN_SELF, value);
    } else {
      this.removeAttribute(attributes.ALIGN_SELF);
    }
  }

  get alignSelf() { return this.getAttribute(attributes.ALIGN_SELF); }

  /**
   * Set the grow setting
   * @param {number|string} value The value
   */
  set grow(value: number | string | null) {
    const val = stringToNumber(value);
    if (!Number.isNaN(val) && FLEX_ITEM_OPTIONS.grow.includes(val)) {
      this.setAttribute(attributes.GROW, String(val));
    } else {
      this.removeAttribute(attributes.GROW);
    }
  }

  get grow() { return this.getAttribute(attributes.GROW); }

  /**
   * Set the shrink setting
   * @param {number|string} value The value
   */
  set shrink(value: number | string | null) {
    const val = stringToNumber(value);
    if (!Number.isNaN(val) && FLEX_ITEM_OPTIONS.shrink.includes(val)) {
      this.setAttribute(attributes.SHRINK, String(val));
    } else {
      this.removeAttribute(attributes.SHRINK);
    }
  }

  get shrink() { return this.getAttribute(attributes.SHRINK); }

  /**
   * Set the flex setting
   * @param {number|string|null} value The value
   */
  set flex(value: number | string | null) {
    const val = stringToNumber(value);
    if (!Number.isNaN(val) && val >= 0 && val <= 24) {
      this.setAttribute(attributes.FLEX, String(val));
    } else {
      this.removeAttribute(attributes.FLEX);
    }
  }

  get flex() { return this.getAttribute(attributes.FLEX); }

  /**
   * Set overflow value on flex item
   * @param {string} val overflow value
   */
  set overflow(val: string | null) {
    if (val && FLEX_ITEM_OPTIONS.overflow.includes(val)) {
      this.setAttribute(attributes.OVERFLOW, val);
    } else {
      this.removeAttribute(attributes.OVERFLOW);
    }
  }

  get overflow(): string | null {
    return this.getAttribute(attributes.OVERFLOW);
  }

  /**
   * Set the min-width of the flex item
   * @param {string} value The value (e.g. '120px', '10rem')
   */
  set minWidth(value: string | null) {
    if (value) {
      this.style.minWidth = value;
      this.setAttribute(attributes.MIN_WIDTH, value.toString());
    } else {
      this.removeAttribute(attributes.MIN_WIDTH);
      this.style.minWidth = '';
    }
  }

  get minWidth() { return this.getAttribute(attributes.MIN_WIDTH); }

  /**
   * Set the paddingX, apply for horizontal sides
   * @param {number|string} value The value
   */
  set paddingX(value: string | null) {
    if (value) {
      this.style.paddingInline = `${value}px`;
      this.setAttribute(attributes.PADDING_X, value.toString());
    } else {
      this.removeAttribute(attributes.PADDING_X);
    }
  }

  /**
   * Get the paddingX
   * @returns {string|null} The paddingX value
   */
  get paddingX() { return this.getAttribute(attributes.PADDING_X); }

  /**
   * Set the paddingY, apply for vertical sides
   * @param {number|string} value The value
   */
  set paddingY(value: string | null) {
    if (value) {
      this.style.paddingBlock = `${value}px`;
      this.setAttribute(attributes.PADDING_Y, value.toString());
    } else {
      this.removeAttribute(attributes.PADDING_Y);
    }
  }

  /**
   * Get the paddingY
   * @returns {string|null} The paddingY value
   */
  get paddingY() { return this.getAttribute(attributes.PADDING_Y); }
}
